import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Users2, Compass, BellRing, UserPlus, ShieldAlert, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loading } from "@/components/common/Loading";
import useGroupsConfig from "@/hooks/useGroupsConfig";
import { GroupCard, MODE_LABEL } from "@/components/groups/GroupCard";
import { CreateGroupDialog } from "@/components/groups/CreateGroupDialog";

function Empty({ title, hint, action }) {
  return (
    <Card className="border-0 flex flex-col items-center gap-2 p-10 text-center">
      <Users2 className="h-8 w-8 text-muted-foreground" />
      <p className="text-base font-semibold">{title}</p>
      <p className="max-w-md text-sm text-muted-foreground">{hint}</p>
      {action}
    </Card>
  );
}

const ROLE_SECTIONS = [
  ["owner", "Groups you own"],
  ["admin", "Groups you administer"],
  ["member", "Groups you're a member of"],
];

/** "in 12 days" / "today" for a deletion countdown (GRP-DEC-219). */
function daysLeft(purgeAt) {
  if (!purgeAt) return "soon";
  const ms = new Date(purgeAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return "soon";
  const days = Math.ceil(ms / 86400000);
  if (days <= 0) return "any time now";
  return `in ${days} day${days === 1 ? "" : "s"}`;
}

/**
 * `/dashboard/groups` — MANAGE MY GROUPS. The member's personal Group workspace in the
 * Dashboard shell: My Groups (by role), Pending (requests I submitted + invitations I
 * received) and Drafts (GRP-DEC-028/029).
 *
 * This is NOT a second directory: public Group discovery lives only at `/groups`. The
 * Group-admin approval queue is not here either — it belongs to the individual Group's
 * management panel (`/dashboard/groups/{id}/manage`).
 *
 * Phase 10 (GRP-DEC-030) — archived and pending-deletion Groups never appear in ordinary
 * navigation, so they get their own Archived tab here, visible to the Owner only. It is the
 * one place a Group whose page is unavailable can be found and restored.
 */
export default function MyGroups() {
  const qc = useQueryClient();
  const { visible, loading } = useGroupsConfig();
  const [tab, setTab] = React.useState("mine");
  const [creating, setCreating] = React.useState(false);

  const mine = useQuery({
    queryKey: ["groups-mine"],
    queryFn: async () => (await api.get("/groups/mine")).data,
    enabled: visible,
  });
  const invites = useQuery({
    queryKey: ["group-invites-mine"],
    queryFn: async () => (await api.get("/group-invites/mine")).data,
    enabled: visible,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["groups-mine"] });
    qc.invalidateQueries({ queryKey: ["group-invites-mine"] });
    qc.invalidateQueries({ queryKey: ["groups-discover"] });
    qc.invalidateQueries({ queryKey: ["groups-pending-request-count"] });
  };

  const withdraw = async (groupId) => {
    try {
      await api.delete(`/groups/${groupId}/request`);
      toast.success("Request withdrawn.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't withdraw that request.");
    }
  };

  // Phase 10 — one action for both archived states: an archived Group goes back to active,
  // and a pending deletion returns to whatever state it was in when deletion was requested.
  const restoreGroup = async (groupId, pending) => {
    try {
      await api.post(`/groups/${groupId}/restore`);
      toast.success(pending ? "Deletion cancelled." : "Group restored.");
      refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't restore that group.");
    }
  };

  if (loading) return <Loading />;
  if (!visible) {
    return <Empty title="Groups aren't available"
                  hint="This area isn't enabled for your account." />;
  }

  const joined = mine.data?.groups || [];
  const drafts = mine.data?.drafts || [];
  const archived = mine.data?.archived || [];
  const pendingRequests = mine.data?.pending_requests || [];
  const pendingInvites = invites.data?.invitations || [];
  const pendingCount = pendingRequests.length + pendingInvites.length;
  // Requests waiting on THIS member as an organizer — a different thing from the Pending
  // tab, which is what the member themselves is waiting on (GRP-DEC-312).
  const actionablePending = mine.data?.actionable_pending_requests || 0;
  // GRP-DEC-313 — the same server-derived shape, a different lifecycle event.
  const actionableNewMembers = mine.data?.actionable_new_members || 0;
  const actionableModeration = mine.data?.actionable_moderation_reports || 0;

  return (
    <div className="space-y-6" data-testid="my-groups-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">My groups</h1>
          <p className="text-sm text-muted-foreground">
            Communities you run and belong to, what you&apos;re waiting on, and your drafts.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" data-testid="my-groups-discover-link">
            <Link to="/groups"><Compass className="mr-2 h-4 w-4" />Discover groups</Link>
          </Button>
          <Button onClick={() => setCreating(true)} data-testid="my-groups-create-button">
            <Plus className="mr-2 h-4 w-4" />Create group
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="my-groups-tabs">
          <TabsTrigger value="mine" data-testid="my-groups-tab-mine">
            My groups {joined.length ? `(${joined.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="my-groups-tab-pending">
            Pending {pendingCount ? `(${pendingCount})` : ""}
          </TabsTrigger>
          <TabsTrigger value="drafts" data-testid="my-groups-tab-drafts">
            Drafts {drafts.length ? `(${drafts.length})` : ""}
          </TabsTrigger>
          {archived.length ? (
            <TabsTrigger value="archived" data-testid="my-groups-tab-archived">
              Archived ({archived.length})
            </TabsTrigger>
          ) : null}
        </TabsList>
      </Tabs>

      {tab === "mine" ? (
        joined.length ? (
          <div className="space-y-6" data-testid="my-groups-list">
            {actionablePending > 0 ? (
              <Card className="border-0 flex flex-wrap items-center gap-2 bg-teal-50/70 p-4"
                    data-testid="my-groups-actionable-banner">
                <BellRing className="h-4 w-4 text-teal-700" />
                <p className="text-sm text-teal-800">
                  <span className="font-semibold">{actionablePending}</span>{" "}
                  membership {actionablePending === 1 ? "request needs" : "requests need"}{" "}
                  your decision across the groups you organize.
                </p>
              </Card>
            ) : null}
            {actionableNewMembers > 0 ? (
              <Card className="border-0 flex flex-wrap items-center gap-2 bg-muted/60 p-4"
                    data-testid="my-groups-new-members-banner">
                <UserPlus className="h-4 w-4 text-teal-700" />
                <p className="text-sm text-foreground">
                  <span className="font-semibold">{actionableNewMembers}</span>{" "}
                  new {actionableNewMembers === 1 ? "member has" : "members have"} joined
                  the groups you organize — say hello.
                </p>
              </Card>
            ) : null}
            {actionableModeration > 0 ? (
              <Card className="border-0 flex flex-wrap items-center gap-2 bg-muted/60 p-4"
                    data-testid="my-groups-moderation-banner">
                <ShieldAlert className="h-4 w-4 text-amber-700" />
                <p className="text-sm text-foreground">
                  <span className="font-semibold">{actionableModeration}</span>{" "}
                  reported {actionableModeration === 1 ? "item needs" : "items need"} your
                  review in the groups you organize.
                </p>
              </Card>
            ) : null}
            {ROLE_SECTIONS.map(([role, label]) => {
              const rows = joined.filter((g) => g.my_role === role);
              if (!rows.length) return null;
              return (
                <div key={role} className="space-y-3" data-testid={`my-groups-section-${role}`}>
                  <h2 className="text-base font-semibold">{label}</h2>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {rows.map((g) => {
                      const needs = role !== "member"
                        ? (g.pending_request_count || 0) + (g.new_member_count || 0)
                          + (g.pending_moderation_count || 0)
                        : 0;
                      const needsLabel = [
                        g.pending_request_count ? `${g.pending_request_count} pending request${g.pending_request_count === 1 ? "" : "s"}` : null,
                        g.new_member_count ? `${g.new_member_count} new member${g.new_member_count === 1 ? "" : "s"}` : null,
                        g.pending_moderation_count ? `${g.pending_moderation_count} report${g.pending_moderation_count === 1 ? "" : "s"} to review` : null,
                      ].filter(Boolean).join(" · ");
                      return (
                      <GroupCard key={g.id} group={g} attention={needs > 0}
                                 attentionLabel={needsLabel} footer={
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="w-fit capitalize">{g.my_role}</Badge>
                          {/* GRP-DEC-312: the actionable pending-request indicator, shown
                              only to an organizer and only when something is waiting. It
                              deep-links straight to that Group's approval queue. */}
                          {role !== "member" && g.pending_request_count > 0 ? (
                            <Button asChild size="sm" variant="ghost"
                                    className="h-6 gap-1 px-2 text-primary hover:text-primary">
                              <Link to={`/dashboard/groups/${g.id}/manage?tab=requests`}
                                    data-testid={`my-groups-pending-${g.id}`}>
                                <BellRing className="h-3.5 w-3.5" />
                                {g.pending_request_count} pending
                                {g.pending_request_count === 1 ? " request" : " requests"}
                              </Link>
                            </Button>
                          ) : null}
                          {/* GRP-DEC-313: NEW MEMBERS — a distinct lifecycle event from a
                              pending request, with its own wording, icon and destination
                              (member management, where it is acknowledged), so the two can
                              never be confused. */}
                          {role !== "member" && g.new_member_count > 0 ? (
                            <Button asChild size="sm" variant="ghost"
                                    className="h-6 gap-1 px-2 text-teal-800 hover:text-teal-900">
                              <Link to={`/dashboard/groups/${g.id}/manage?tab=members`}
                                    data-testid={`my-groups-new-members-${g.id}`}
                                    aria-label={`${g.new_member_count} new member${g.new_member_count === 1 ? "" : "s"} in ${g.display_name} — review members`}>
                                <UserPlus className="h-3.5 w-3.5" />
                                {g.new_member_count} new
                                {g.new_member_count === 1 ? " member" : " members"}
                              </Link>
                            </Button>
                          ) : null}
                          {/* Phase 7 — reports awaiting the organizer's review. Its own
                              wording, icon and destination (Manage → Moderation). */}
                          {role !== "member" && g.pending_moderation_count > 0 ? (
                            <Button asChild size="sm" variant="ghost"
                                    className="h-6 gap-1 px-2 text-amber-800 hover:text-amber-900">
                              <Link to={`/dashboard/groups/${g.id}/manage?tab=moderation`}
                                    data-testid={`my-groups-moderation-${g.id}`}
                                    aria-label={`${g.pending_moderation_count} report${g.pending_moderation_count === 1 ? "" : "s"} to review in ${g.display_name}`}>
                                <ShieldAlert className="h-3.5 w-3.5" />
                                {g.pending_moderation_count}
                                {g.pending_moderation_count === 1 ? " report" : " reports"}
                              </Link>
                            </Button>
                          ) : null}
                          {role !== "member" ? (
                            <Button asChild size="sm" variant="ghost">
                              <Link to={`/dashboard/groups/${g.id}/manage`}
                                    data-testid={`my-groups-manage-${g.id}`}>Manage</Link>
                            </Button>
                          ) : null}
                        </div>} />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Empty title="You're not in any groups yet"
                 hint="Browse the Groups directory to find a community, or create your own."
                 action={<Button asChild variant="outline" data-testid="my-groups-empty-discover">
                   <Link to="/groups">Discover groups</Link>
                 </Button>} />
        )
      ) : null}

      {tab === "pending" ? (
        <div className="space-y-4" data-testid="my-groups-pending-panel">
          {pendingInvites.length ? (
            <div className="space-y-2">
              <h2 className="text-base font-semibold">Invitations you received</h2>
              {pendingInvites.map((inv) => (
                <Card key={inv.id} className="border-0 flex flex-wrap items-center justify-between gap-3 p-4"
                      data-testid={`group-invite-row-${inv.id}`}>
                  <div>
                    <Link to={inv.group_url || "#"} className="font-medium hover:text-teal-700">
                      {inv.group_name}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      Invited by {inv.invited_by_name || "a member"} · {MODE_LABEL[inv.membership_mode]}
                    </p>
                  </div>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/dashboard/invitations" data-testid={`group-invite-review-${inv.id}`}>
                      Review
                    </Link>
                  </Button>
                </Card>
              ))}
            </div>
          ) : null}
          {pendingRequests.length ? (
            <div className="space-y-2">
              <h2 className="text-base font-semibold">Requests you submitted</h2>
              {pendingRequests.map((g) => (
                <Card key={g.id} className="border-0 flex flex-wrap items-center justify-between gap-3 p-4"
                      data-testid={`group-pending-row-${g.id}`}>
                  <div>
                    <Link to={g.url || "#"} className="font-medium hover:text-teal-700">
                      {g.display_name}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      Waiting on the organizers
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => withdraw(g.id)}
                          data-testid={`group-withdraw-${g.id}`}>
                    Withdraw
                  </Button>
                </Card>
              ))}
            </div>
          ) : null}
          {!pendingCount ? (
            <Empty title="Nothing pending"
                   hint="Requests you submit and invitations you receive show up here." />
          ) : null}
        </div>
      ) : null}

      {tab === "drafts" ? (
        drafts.length ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="my-groups-drafts-list">
            {drafts.map((g) => (
              <Card key={g.id} className="border-0 space-y-2 p-4" data-testid={`group-draft-${g.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold">{g.display_name || "Untitled group"}</p>
                  <Badge variant="secondary">Draft</Badge>
                </div>
                <p className="line-clamp-2 text-sm text-muted-foreground">{g.short_description}</p>
                <p className="text-xs text-muted-foreground">
                  Not published — drafts never appear in the Groups directory.
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to={`/dashboard/groups/${g.id}/manage`} data-testid={`group-draft-open-${g.id}`}>
                    Finish setup
                  </Link>
                </Button>
              </Card>
            ))}
          </div>
        ) : (
          <Empty title="No drafts" hint="Groups you save before publishing appear here." />
        )
      ) : null}

      {tab === "archived" ? (
        archived.length ? (
          <div className="space-y-3" data-testid="my-groups-archived-list">
            {archived.map((g) => (
              <Card key={g.id} className="border-0 flex flex-wrap items-center justify-between gap-3 p-4"
                    data-testid={`group-archived-row-${g.id}`}>
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{g.display_name || "Untitled group"}</p>
                    <Badge variant={g.pending_deletion ? "destructive" : "secondary"}
                           data-testid={`group-archived-badge-${g.id}`}>
                      {g.pending_deletion ? "Pending deletion" : "Archived"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {g.pending_deletion
                      ? `Permanently deletes ${daysLeft(g.purge_at)} — restore before then to keep everything.`                      : "Hidden from discovery and unavailable to members. Nothing has been deleted."}
                  </p>
                </div>
                <Button size="sm" variant={g.pending_deletion ? "default" : "outline"}
                        data-testid={`group-restore-${g.id}`}
                        onClick={() => restoreGroup(g.id, g.pending_deletion)}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  {g.pending_deletion ? "Cancel deletion" : "Restore group"}
                </Button>
              </Card>
            ))}
          </div>
        ) : (
          <Empty title="Nothing archived"
                 hint="Groups you archive or schedule for deletion appear here." />
        )
      ) : null}

      <CreateGroupDialog open={creating} onOpenChange={setCreating} onCreated={refresh} />
    </div>
  );
}
